import {
  APP_INITIALIZER,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { HubbixCommonConfigurationModule } from '@hubbix/common-components/configuration';
import { HubbixCommonMultitenancyModule } from '@hubbix/common-components/multitenancy';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApmModule } from '@elastic/apm-rum-angular';
import { provideErrorHandlers } from '@hubbix/common-components/errors';
import { provideRouter, Router } from '@angular/router';
import { provideApm } from '@hubbix/common-components/apm';
import { routes } from './app.routes';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import fr from '@angular/common/locales/fr';
import { NZ_I18N, fr_FR, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import { VisionErrorHandler } from './core/handler/vision-error-handlers';
import { httpInterceptorProviders } from './core/interceptors';
import { CredentialsInterceptor } from './core/interceptors/credentials.interceptor';
import { FooterUrlService } from './core/services/footerUrl.service';
import { GlobalsService } from './core/services/globals.service';
import { notificationDataOptions } from './shared/constants/notification.constants';
import { NOTIFICATION_DATA_OPTIONS } from './shared/injections/notification-data-options.token';
import { ConfigurationService } from './shared/services/configuration.service';
import {
  initializeConsentManagerScript,
  ConsentManagerCookiesService,
} from './shared/services/consent-manager-cookies.service';
import {
  initializeIdForRedirectionFromApp,
  RedirectionFromAppService,
} from './shared/services/redirection-from-app.service';
import { initializeTrialPeriodFactory, TrialPeriodService } from './shared/services/trial-period.service';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { NgProgressModule } from 'ngx-progressbar';
import { EBP_FIRST_CONNECTION_KEY } from './shared/constants/storage-keys.constant';
import { provideAuthentication } from '@core/authentication/providers/authentication.provider';

function getDefaultLanguage(): 'fr' | 'en' {
  return document.cookie.includes('.Ebp.Culture=c=en|uic=en') ? 'en' : 'fr';
}

function registerLocale(defaultLang: string): void {
  switch (defaultLang) {
    case 'en':
      registerLocaleData(localeEn, 'en', localeEnExtra);
      break;
    case 'fr':
    default:
      registerLocaleData(localeFr, 'fr', localeFrExtra);
      break;
  }
}

function initializeApp(): () => void {
  return () => {
    if (!document.cookie.includes('.Ebp.Culture')) {
      document.cookie = `.Ebp.Culture=c=fr|uic=fr`;
    }
    if (!localStorage.getItem(EBP_FIRST_CONNECTION_KEY)) {
      localStorage.setItem(EBP_FIRST_CONNECTION_KEY, 'true');
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      HubbixCommonConfigurationModule.forRootWithLoadConfiguration(),
      HubbixCommonMultitenancyModule.forRoot(),
      BrowserAnimationsModule,
      ApmModule,
      NzModalModule,
      NgProgressModule.withConfig({
        color: '#00bfa5',
      }),
      NgProgressHttpModule,
    ),
    provideErrorHandlers({ withNotification: true, mode: 'stderr' }),
    provideApm(),
    provideAuthentication(),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),

    // Locale & Date settings
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const defaultLang = getDefaultLanguage();
        registerLocale(defaultLang);
        return defaultLang;
      },
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useFactory: () => (getDefaultLanguage() === 'en' ? 'USD' : 'EUR'),
    },
    { provide: NZ_I18N, useValue: fr_FR },
    { provide: NZ_DATE_LOCALE, useValue: fr },

    // Global error handler
    { provide: ErrorHandler, useClass: VisionErrorHandler },

    // Interceptors
    httpInterceptorProviders,
    { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },

    // Services
    { provide: Window, useValue: window },
    { provide: FooterUrlService, useClass: FooterUrlService },
    {
      provide: NOTIFICATION_DATA_OPTIONS,
      useFactory: () => ({ ...notificationDataOptions }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTrialPeriodFactory,
      deps: [Router, TrialPeriodService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIdForRedirectionFromApp,
      deps: [Router, RedirectionFromAppService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigurationService) => async () => configService.load().toPromise(),
      deps: [ConfigurationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (globalsService: GlobalsService) => async () => globalsService.load().toPromise(),
      deps: [GlobalsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeConsentManagerScript,
      deps: [ConsentManagerCookiesService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      multi: true,
    },
  ],
};
